import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import PropTypes from 'prop-types';

//import { useAppContext } from '../context/AppContext';  // 引入 useAppContext
import { useSound } from '../App';
import { Order } from '../types';

import { t } from '../i18n/i18n'; // 根据实际文件路径调整

//============================================================================
// 直接修改order信息而未调用上级回调。order是父组件乃至从最上上级组件传入的引用。
// 我修改的信息都是order.js组件自己负责，并不需要它的父组件及更高级渲染，所以偷个懒。
// react中父组件子组件等多层组件的渲染都是在一个线程，所以父组件里的handleDeleteOrder（onDeleteOrder）
// 产生了新的orders，而order组件内部的修改也都会在的。
const OrderLine = ({ order, revisionStatus, onDeleteOrder }) => {

    //========================================================================
    const [playSound] = useSound('/goldcoin.mp3');

    //========================================================================
    // 判断是否允许编辑（只有在revision状态为prepare时才允许）
    const isEditable = revisionStatus === 'prepare';

    // 右键点击时显示删除确认框
    //const { isDeleteModalOpen, setIsDeleteModalOpen } = useAppContext();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);  // 删除确认框状态
    const [currentOrder, setCurrentOrder] = useState(null); // 当前选中的订单
    const handleRightClick = (e) => {
        playSound();
        e.preventDefault(); // 防止默认右键菜单弹出
        setCurrentOrder(order); // 设置当前右键点击的订单
        setIsDeleteModalOpen(true); // 打开删除确认框
    };
    const handleDeleteOrder = () => {
        onDeleteOrder(currentOrder); // 调用父组件的回调函数来删除订单
        setIsDeleteModalOpen(false); // 关闭删除确认框
    };
    const handleCancelDelete = () => {
        setIsDeleteModalOpen(false); // 取消删除，关闭确认框
    };

    //========================================================================
    // // 作用：1）强制调父的回调函数刷新父组件显示；2）而且也是防止外部更新这些值。不过目前暂时没有必要。而且会导致双重渲染
    // useEffect(() => {
    //     ......;
    //     ......;
    // }, [
    //     ......;
    //     ......;
    // ]);
    
    //========================================================================
    // 获取第一列的文本
    const getFirstColumnText = () => {
        //if(order.type in ['entryPoint', 'close', 'takeProfit' ) {
        if (['entryPoint', 'close', 'takeProfit'].includes(order.type)) {
            return (orderType === 'market')
                ? `${t(order.type)}${t('market')}`
                : `${t(order.type)}`;
        } 
        else if (order.type === 'stopLoss') {
            return useEntryPrice 
                ? `${t('stopLoss')}(${t('useEntryPrice')})` 
                : t('stopLoss');
        }
        return t(order.type);
    };

    // 判断是否显示暗灰斜体样式
    const getOrderLineClass = () => {
        if (order.type === 'entryPoint' || order.type === 'stopLoss' || order.type === 'close') {
            return order.useEntryPrice || orderType === "market" ? "order-highlight" : "";
        }
        return "";  // 其他类型不变
    }
    
    //========================================================================
    // 双击第一列，切换orderType。只有order.type为entryPoint和close的才需要紧急的market的orderType
    const [orderType, setOrderType] = useState(order.orderType);
    const handleDoubleClickMarket = () => {
        playSound();
        const newOrderType = orderType === 'market' ? '' : 'market';
        setOrderType(newOrderType);  // 更新orderType的状态
        order.orderType = newOrderType; // 同时更新原始订单的orderType。js对象操作里，如果 属性（order） 不存在，属性（order） 会被创建被添加
    };

    //========================================================================
    // 双击第一列且为stopLoss时，切换useEntryPrice
    const [useEntryPrice, setUseEntryPrice] = useState(order.useEntryPrice);
    const handleDoubleClickStopLoss = () => {
        playSound();
        const newUseEntryPrice = !useEntryPrice;
        setUseEntryPrice(newUseEntryPrice); // 更新 useEntryPrice
        order.useEntryPrice = newUseEntryPrice; // 同时更新原始订单的 useEntryPrice
    };

    //========================================================================
    const [isEditingAmount, setIsEditingAmount] = useState(false);
    const [tempAmount, setTempAmount] = useState(order.assetAmount);
    // 处理amount输入，支持数字和百分比，保持原格式
    const handleAmountChange = (e) => {
        let value = e.target.value;
        // 将全角的“。”替换为半角的“.”
        value = value.replace(/。/g, '.');
        const validPattern = /^[0-9]*\.?[0-9]*%?$/;// 只允许输入数字、小数点和可选的百分号。TODO：以后可以改成输入时固化百分号。
        if ( validPattern.test(value) ) {
            setTempAmount(value); // 更新输入值
        } else if (value === "") {
            // 允许空值，用户清空输入时
            setTempAmount(value);
        }
    };
    // 双击金额字段进行编辑
    const handleDoubleClickAmount = () => {
        playSound();
        setIsEditingAmount(true);
    };
    // 失去焦点时保存输入并退出编辑模式
    const handleBlurAmount = () => {
        playSound();
        setIsEditingAmount(false);
        // 确保保存的仍然是百分比或数字格式
        if (tempAmount !== order.assetAmount) {
            order.assetAmount = tempAmount;
        }
    };
    // 监听回车键来确认输入并退出编辑
    const handleKeyDownAmount = (e) => {
        if (e.key === 'Enter') {
            handleBlurAmount(); // 按回车键时触发保存操作并退出编辑
        }
    };

    //========================================================================
    const [isEditingPx, setIsEditingPx] = useState(false);
    const [tempPx, setTempPx] = useState(order.orderPx);
    const handleDoubleClickPx = () => {
        playSound();
        setIsEditingPx(true);
    };
    const handleBlurPx = () => {
        playSound();
        setIsEditingPx(false);
        if (tempPx !== order.orderPx) {
            order.orderPx = tempPx;
        }
    };

    // 监听回车键来确认价格并退出编辑
    const handleKeyDownPx = (e) => {
        if (e.key === 'Enter') {
            handleBlurPx(); // 按回车键时触发保存操作并退出编辑
        }
    };

    //========================================================================
    return (
        <>
            <tr
                className={`order-line ${getOrderLineClass()}`}
                onContextMenu={isEditable ? handleRightClick : null} // 右键点击事件
            >
                {/* 第一列，双击时触发 market 切换 */}
                <td 
                    onDoubleClick={isEditable && (order.type === 'close' || order.type === 'entryPoint' || order.type === 'stopLoss') 
                        ? (order.type === 'stopLoss' ? handleDoubleClickStopLoss : handleDoubleClickMarket ) 
                        : null}
                    className="editable-field"
                >
                    {getFirstColumnText()}
                </td>
                <td
                    onDoubleClick={isEditable ? handleDoubleClickPx : null}
                    className="editable-field"
                >
                    {isEditingPx ? (
                        <input 
                            type="number"
                            value={tempPx}
                            onChange={(e) => setTempPx(e.target.value)}
                            onBlur={handleBlurPx}
                            onKeyDown={handleKeyDownPx} // 监听回车键
                            autoFocus
                            step="any"  // 支持任意小数
                            className="no-arrows"                   
                        />
                    ) : (
                        //order.orderPx || `?${t("market")}?`
                        order.orderPx || ` - `
                    )}
                </td>
                <td 
                    onDoubleClick={isEditable ? handleDoubleClickAmount : null}
                    className="editable-field"
                >
                    {isEditingAmount ? (
                        <input 
                            type="text"  // 改为text类型来处理百分比和数字
                            value={tempAmount}
                            onChange={handleAmountChange}  // 处理输入
                            onBlur={handleBlurAmount}
                            onKeyDown={handleKeyDownAmount} // 监听回车键
                            autoFocus
                            className="no-arrows"
                        />
                    ) : (
                        order.assetAmount || ` - `
                    )}
                </td>
            </tr>

            {/* 删除确认框 */}
            {isDeleteModalOpen && currentOrder === order && createPortal(
                <div className="modal-overlay" onClick={handleCancelDelete}>
                    <div className="modal status-modal">
                        <button className="close-btn" onClick={handleCancelDelete}>×</button>
                        <p style={{  fontSize: '20px', color: 'red', fontWeight: 'bold' }}>{t('Delete')}</p>
                        <p>{t('AreYouSure?')}</p>
                        <div className="modal-buttons">
                            <button onClick={handleDeleteOrder} className="confirm-button">{t('Confirm')}</button>
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            <button onClick={handleCancelDelete} className="concel-button">{t('Cancel')}</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

        </>
    );
};

//============================================================================
OrderLine.propTypes = {
    order: Order.isRequired,
    revisionStatus: PropTypes.string.isRequired,
    onDeleteOrder: PropTypes.func.isRequired,  // 接收父组件传入的删除回调
};

//============================================================================
export default OrderLine;
